import {
    SkillDrills, skillsForProvider, FirefighterRanks, ensureJSDates,
    levelCerts as commonLevelCerts, outstandingLevelCerts,
    drillsNeededForEvaluationForm, meetsEvaluationFormThreshold, qualificationCellAt, isHiddenAccount
} from '@embertracking/common';
import {isCellSignedOff} from '@embertracking/ui/data/skills';
import {callFunction} from '-/data/utils';

// Skill-drill tracking — client data layer + pure derivation over users.skills.
// The hardcoded SkillDrills catalogue (common/skillDrills.js) maps each drill/exam
// to its Viera qualification cell; everything the section pages render is DERIVED
// from a member's users.skills projection. See #1004.

// Firefighter progression ranks — the population the skill-drill sections track.
// Officers above Full Service are already qualified and don't appear.
export const PROGRESSION_RANKS = ['RECRUIT', 'EXTERIOR', 'INTERIOR', 'FULLSERVICE'];

// The progression ladders, in order, per training provider. A ladder's sections imply
// each other by `order` (finishing Interior satisfies Exterior), and a section can't be
// signed off until the rungs below it are — see isSectionComplete / pendingPriorSections,
// both of which scan `orderedSections()` for the section's own ladder. JIBC has no drill
// sections.
export const LADDER_SECTION_KEYS = {
    viera: ['exterior', 'interior', 'fullService'],
    vieraGeneric: ['exterior', 'interior', 'fullService'],
    nfpa1010: ['ff1', 'ff2'],
    cotr: ['cotrExterior', 'cotrInterior', 'cotrFullService']
};

// Every ladder section key across providers — the set the router mounts.
export const SECTION_KEYS = [...new Set(Object.values(LADDER_SECTION_KEYS).flat())];

// The ladder a section is a rung of. The generated VIERA sections predate the field.
const ladderOf = section => section?.ladder || 'viera';
export const levelKeyOf = section => section?.levelKey || section?.key;

// Sections that are NOT rungs on that ladder — parallel qualifications a member can
// hold at any point. Deliberately kept out of SECTION_KEYS rather than filtered out
// inside each helper: the cascade helpers scan `orderedSections()`, so a standalone
// section is structurally unable to imply (or be implied by) a progression level.
// See common/skillDrillsDriverOperator.js (#1579).
export const STANDALONE_SECTION_KEYS = ['driverOperator'];

const bindSectionProvider = (section, trainingProvider) => {
    if (!section || !trainingProvider) return section || null;
    return {
        ...section,
        trainingProvider,
        chapters: (section.chapters || []).map(chapter => ({...chapter, trainingProvider}))
    };
};

export const getSection = (sectionKey, trainingProvider) =>
    bindSectionProvider(SkillDrills[sectionKey], trainingProvider);

// One provider's progression ladder only. This is what the cascade helpers scan, so it
// must never include a standalone section — or another provider's rungs, which would
// let a VIERA Interior sign-off imply Firefighter I. Defaults to VIERA's, the ladder
// every caller meant before there was more than one.
export const orderedSections = (trainingProvider = 'viera') =>
    (LADDER_SECTION_KEYS[trainingProvider] || []).map(k => getSection(k, trainingProvider)).filter(Boolean);

const ladderSections = section => orderedSections(ladderOf(section));

// Every section in the catalogue, ladder and standalone alike, unbound to a provider.
// For surfaces that just need the set that exists — breadcrumbs, titles — rather than
// the set a given department shows (displaySections) or the progression (orderedSections).
export const allSections = () => [...SECTION_KEYS, ...STANDALONE_SECTION_KEYS]
    .map(k => SkillDrills[k])
    .filter(Boolean);

// Every section to show in the UI: the department's own progression
// ladder (VIERA's only for VIERA departments — #1086 — and 1010's only for 1010 ones)
// plus standalone sections, which apply to any provider because their backing cell
// exists in every catalogue.
export const displaySections = trainingProvider => [
    ...(LADDER_SECTION_KEYS[trainingProvider] ? orderedSections(trainingProvider) : []),
    ...STANDALONE_SECTION_KEYS.map(k => getSection(k, trainingProvider)).filter(Boolean)
];

// The provider name shown on generated Requirements rows. Both J&B views belong to the
// College of the Rockies; `nfpa1010` and `cotr` only distinguish their ladder layouts.
export const sectionProgramLabel = section => ({
    cotr: 'COTR',
    nfpa1010: 'COTR',
    viera: 'VIERA'
}[section?.ladder] || 'VIERA');

export const sectionRequirementCategory = section =>
    sectionProgramLabel(section) === 'COTR'
        ? 'College of the Rockies'
        : `${sectionProgramLabel(section)} Qualification`;

// --- per-cell state -------------------------------------------------------

const cellOf = (user, chapter) => qualificationCellAt(
    user?.skills,
    `${chapter.group}.${chapter.skillKey}`,
    chapter.trainingProvider
) || {};

// A practical drill is 'signed' when its entry under the cell's practical.drills is done.
export const drillSigned = (user, chapter, code) => Boolean(cellOf(user, chapter).practical?.drills?.[code]?.done);

export const drillSignoff = (user, chapter, code) => cellOf(user, chapter).practical?.drills?.[code] || null;

// Drill outcome state, mirroring the JPR sheet's first-test/retest columns:
//   untested → no attempt yet
//   passed   → latest attempt is a PASS (first test or retest)
//   retest   → first test failed, no retest recorded yet (needs a retest)
//   failed   → retest also failed
export const drillState = (user, chapter, code) => {
    const rec = cellOf(user, chapter).practical?.drills?.[code];
    if (!rec) return 'untested';
    if (rec.done) return 'passed';
    if (rec.retest) return 'failed';
    return 'retest';
};

// A theory exam is 'passed' when the cell's theory half has a completion date.
// All chapters in an exam are written together, so any chapter in the exam answers it.
export const theoryPassed = (user, chapter) => Boolean(cellOf(user, chapter).theory?.completedDate);

// Driver/Operator's chapters all share ONE cell, so its per-branch exams are stored in a
// map keyed by exam (`theory.exams[<exam>]`) instead of at `theory.completedDate` — three
// branch exams in one `theory` half would otherwise overwrite each other. Sections
// declare this with `examsShareCell`; everything else keeps the original single-exam
// storage. See common/skillDrillsDriverOperator.js.
export const examSignoff = (user, section, exam) => {
    const chapters = examChapters(section, exam);
    if (!chapters.length) return null;
    const cell = cellOf(user, chapters[0]);
    if (section.examsShareCell) return cell.theory?.exams?.[exam] || null;
    return cell.theory || null;
};

// A whole section is "checked off" when its Viera level roll-up cell
// (skills.{level}.status / completedDate) is complete — the same cell the
// Qualification Matrix level column reads and an officer sets via the
// MemberTraining button group. This, NOT the sum of individual drills, is the
// source of truth for whether a member has finished a section and moved on.
// Drills/exams roll up to in-progress chapter cells; checking off the level is
// the deliberate sign-off that graduates the member to the next section.
// A standalone section has no level roll-up cell of its own — Driver/Operator isn't a
// VIERA level, it's a single skill cell inside Truck Ready (or Exterior on JIBC). Such a
// section names its own completion cell instead, and that cell is what the matrix
// column renders, so the two can't disagree.
export const sectionCompletionCell = (user, section) => {
    const {completionCell} = section || {};
    if (completionCell) {
        return qualificationCellAt(
            user?.skills,
            `${completionCell.group}.${completionCell.skillKey}`,
            section.trainingProvider
        );
    }
    return user?.skills?.[levelKeyOf(section)];
};

// Shared with the matrix renderer and the MemberTraining dialog (isCellSignedOff, in
// @embertracking/ui/data/skills) so a cell can't read signed off on one surface and
// in-progress on another.
export const sectionCheckedOff = (user, section) => {
    const cell = sectionCompletionCell(user, section);
    if (!cell) return false;
    return isCellSignedOff(cell);
};

// --- chapter / exam / section progress ------------------------------------

export const chapterPractical = (user, chapter) => {
    const total = chapter.drills.length;
    const done = chapter.drills.filter(d => drillSigned(user, chapter, d.code)).length;
    return {done, total, complete: total > 0 && done === total};
};

// Who may go on a chapter's pre-filled evaluation summary (#1014, #1698).
//
// The per-chapter summary is the artifact departments submit to VIERA, so it can't carry
// a member who hasn't demonstrated enough of the chapter — they'd print as a half-blank
// row that reads as a failure. "Enough" is VIERA's own bar, 60% of the chapter's JPRs
// (see meetsEvaluationFormThreshold). The api applies the identical rule server-side and
// is the authority.
export const chapterSubmittableMembers = (users, chapter) => (
    (users || []).filter(u => {
        const {done, total} = chapterPractical(u, chapter);
        return meetsEvaluationFormThreshold(done, total);
    })
);

// The rows the summary's member picker renders (#1698): each member's progress through
// this chapter, what they'd need to clear the threshold, and whether they clear it.
//
// Only members with at least one drill signed off in THIS chapter are listed. The pool
// this draws from is the section roster, which includes every member already signed off
// at level — and on most departments that's mostly members whose qualification was
// recorded on the level cell rather than demonstrated drill by drill (a lateral hire
// credited through "Already qualified", or a record migrated from before the sheet
// existed). They have no per-drill evidence to print, are never submittable, and aren't
// working towards this chapter either, so listing them buries the handful of members the
// picker exists to choose between under a wall of rows reading "0/2 · 2 drills
// outstanding" — for members the Assignments tab calls Complete. The dialog reports how
// many were left out rather than silently shortening the list.
export const chapterFormRows = (users, chapter) => (users || [])
    .map(member => {
        const {done, total, complete} = chapterPractical(member, chapter);
        return {
            uid: member.uid,
            member,
            done,
            total,
            complete,
            needed: drillsNeededForEvaluationForm(total),
            submittable: meetsEvaluationFormThreshold(done, total),
            pct: total ? Math.round((done / total) * 100) : 0
        };
    })
    .filter(row => row.done > 0);

// One exam can span several chapters; count it once, against the first chapter that
// carries it. Returns the chapters (in order) the exam covers.
export const examChapters = (section, exam) => section.chapters.filter(c => c.exam === exam);

export const examPassed = (user, section, exam) => {
    const chapters = examChapters(section, exam);
    if (!chapters.length) return false;
    if (section.examsShareCell) return Boolean(examSignoff(user, section, exam)?.completedDate);
    return theoryPassed(user, chapters[0]);
};

// Chapters that count toward this member's progress.
//
// Most sections: all of them. A section marked `conditionalChapters` (Driver/Operator)
// mirrors the OFC standard, which is conditional — every large-apparatus driver needs
// EVD, but the pump / mobile-water-supply branches apply only to drivers who operate
// that equipment. There's no per-member "operates a pump" flag, so applicability is
// inferred: a branch nobody has signed anything on is treated as not applicable rather
// than as 0%. Without this a driver of a non-pump utility vehicle can never reach 100%,
// so their cell can never be completed (#1579).
//
// The first chapter is always applicable — it's the universal branch, and it keeps a
// member with nothing signed from having an empty (and therefore vacuously complete)
// requirement set.
//
// A branch is in scope once ANY of its evidence exists — a signed drill or a passed
// written assessment. Keying off drills alone would leave a member who sat the pump exam
// first with an out-of-scope branch, and their exam pass uncounted.
export const applicableChapters = (user, section) => {
    const chapters = section?.chapters || [];
    if (!section?.conditionalChapters) return chapters;
    return chapters.filter((ch, index) =>
        index === 0 ||
        ch.drills.some(d => drillSigned(user, ch, d.code)) ||
        (ch.exam !== undefined && examPassed(user, section, ch.exam)));
};

export const isChapterApplicable = (user, section, chapter) =>
    applicableChapters(user, section).some(ch => ch.chapter === chapter.chapter);

// Exams that count for this member — those carried by an applicable chapter. On a
// non-conditional section that's every exam, unchanged.
export const applicableExams = (user, section) => {
    const chapters = applicableChapters(user, section);
    return (section?.exams || []).filter(ex => chapters.some(ch => ch.exam === ex.exam));
};

export const isExamApplicable = (user, section, exam) =>
    applicableExams(user, section).some(ex => ex.exam === exam);

// Section completion = the level cell is checked off (see sectionCheckedOff), OR a
// higher-ordered level is — completing Interior (or Full Service) implicitly
// satisfies Exterior, so a member who's progressed past a section shouldn't keep
// surfacing on its roster. Mirrors the cascade the Qualification Matrix shows.
// (`ladderSections()` is the section's own progression ladder only, so a standalone
// section is neither implied by nor implies a level — it just reads its own completion
// cell.)
export const isSectionComplete = (user, section) =>
    sectionCheckedOff(user, section) ||
    (!section?.standalone && ladderSections(section).some(s => s.order > section.order && sectionCheckedOff(user, s)));

// The higher level that satisfies this section, when the section's own cell isn't signed
// off — for the surfaces that have to say WHY a member reads complete with nothing signed
// under them, and to know that "Reopen" has nothing to reopen. Null when the section owns
// its own completion.
export const impliedBySection = (user, section) => {
    if (!section || section.standalone || sectionCheckedOff(user, section)) return null;
    return ladderSections(section).find(s => s.order > section.order && sectionCheckedOff(user, s)) || null;
};

// Whole-section progress for one member: every drill + every (distinct) exam
// counts as one item, driving the percentage shown on the progress bars. The
// `complete`/`state` fields key off the checked-off level cell (NOT the drill
// sum) so a member at 100% drills still reads "in progress" until an officer
// checks off the section — and a checked-off section reads "complete" even if a
// drill was logged elsewhere. Returns counts, percent, and the member state.
export const sectionProgress = (user, section) => {
    let signed = 0;
    let total = 0;
    applicableChapters(user, section).forEach(ch => {
        ch.drills.forEach(d => {
            total++;
            if (drillSigned(user, ch, d.code)) signed++;
        });
    });
    applicableExams(user, section).forEach(ex => {
        total++;
        if (examPassed(user, section, ex.exam)) signed++;
    });
    // isSectionComplete, NOT sectionCheckedOff: a member whose Interior is signed off has
    // finished Exterior too, and every surface has to agree about that. The roster split
    // and the Reopen button already used the cascading form while the status chip and the
    // progress bar used this one, which is what put a member in the Completed bucket
    // labelled "Not started · 0%" with a Reopen button beside it (#1622).
    //
    // Declared above this function deliberately — these are const arrow functions, so they
    // are not hoisted.
    const complete = isSectionComplete(user, section);
    // A member an officer has signed off is done, whatever the drill records hold — the
    // cell, not the sum of items, is the source of truth for the qualification (see
    // sectionCheckedOff). Without this a legacy member migrated with a COMPLETE cell and
    // no per-item records read "0% · Complete", which looks like a bug rather than a
    // record predating the sheet.
    const pct = complete ? 100 : (total ? Math.round((signed / total) * 100) : 0);
    const state = complete ? 'complete' : (signed === 0 ? 'not-started' : 'in-progress');
    return {signed, total, pct, state, complete};
};

// Every drill demonstrated and every exam passed.
export const sectionFullyDemonstrated = (user, section) => {
    const {signed, total} = sectionProgress(user, section);
    return total > 0 && signed === total;
};

// The department's resolved skills catalogue — the same provider catalogue the api and
// buildSkills() in ui/data/skills.js resolve. Per-department custom categories are left
// out: they're `extra`, so never on a ladder.
const catalogueFor = skillsForProvider;

// All non-drill level certs with their completion state — for the member detail view.
// Spans every level this section's sign-off implies, so Exterior surfaces Truck Ready's
// provincial certs on VIERA; each entry carries the `level` it lives on so the caller can
// deep-link the right matrix cell (#1450).
export const levelCerts = (user, section, trainingProvider) =>
    commonLevelCerts(user?.skills, levelKeyOf(section), trainingProvider, catalogueFor(trainingProvider));

// Non-drill certifications on the level (e.g. NFPA 1072 Hazmat, ICS 100, WSPP-WFF 1) that
// aren't skill drills but are still required to qualify. Returns the labels of any that
// are still outstanding for this member. Deliberate mirror of the server's gate in
// api/src/routes/skillDrills.js — both call the same helper in common so they can't drift.
export const pendingLevelCerts = (user, section, trainingProvider) =>
    outstandingLevelCerts(user?.skills, levelKeyOf(section), trainingProvider, catalogueFor(trainingProvider));

// The lower rungs on this section's ladder the member hasn't finished yet. Every section
// rosters every member who hasn't completed it — an Exterior member picks up Interior
// skills at a live-fire weekend that covers Live Fire 1 and 2 at once, and those have to
// be signable where they happen. But the SIGN-OFF still climbs the ladder in order:
// completing Interior implies Exterior (isSectionComplete), so letting it through with
// Exterior outstanding would quietly qualify the member for a level nobody signed off.
// Mirrored by the api's section sign-off. A standalone section isn't on the ladder, so
// nothing sits below it.
export const pendingPriorSections = (user, section) => {
    if (!section || section.standalone) return [];
    return ladderSections(section).filter(s => s.order < section.order && !isSectionComplete(user, s));
};

// The section can be signed off only when every drill + exam is demonstrated, every
// level cert is complete, and every lower rung is signed off.
export const canCompleteSection = (user, section, trainingProvider) =>
    sectionFullyDemonstrated(user, section)
    && pendingLevelCerts(user, section, trainingProvider).length === 0
    && pendingPriorSections(user, section).length === 0;

// Only firefighter ranks owe skill drills — this mirrors the Qualification Matrix,
// which queries `where('role', 'in', FirefighterRanks)` so non-firefighter staff
// (Employees, Apparatus, Administrators, station logins) never appear. Officers are
// in FirefighterRanks, so a member promoted before finishing a VIERA level still
// shows; completion then filters from skills. Hidden accounts (the operator, hub
// users) aren't department members — excluded everywhere else (Members, ImportUsers,
// the users context), so keep them off the roster too.
export const isTrackableMember = u => !!u && !u.archived && u.registered !== false && !u.isStation
    && FirefighterRanks.includes(u.role) && !isHiddenAccount(u);

// Roster tally for one section: how many of the members it covers have finished it.
// Feeds the completion column on the requirements list, and is deliberately the same
// population the section page averages its Completion card over — every trackable member,
// minus the ones an officer hid (hiding is a "not my problem" flag, so they shouldn't
// drag the denominator), plus everyone already signed off. Same cascading completion
// rule, so the two pages agree.
export const sectionRosterTally = (members, section) => {
    if (!section) return null;
    const covered = (members || [])
        .filter(isTrackableMember)
        .filter(m => isSectionComplete(m, section) || !isSectionHidden(m, section));
    return {
        assigned: covered.length,
        completed: covered.filter(m => isSectionComplete(m, section)).length,
        // Members part-way through — at least one drill or exam signed, not finished.
        // `sectionProgress().state` is what the section page's own In-progress card
        // counts, so the list's bar splits the roster exactly the way that page does.
        inProgress: covered.filter(m => sectionProgress(m, section).state === 'in-progress').length
    };
};

// --- mutations ------------------------------------------------------------

export const signOffSkillDrill = params => callFunction('signOffSkillDrill', params);

// The api payload recording one practical evaluation. The marks go with the request
// deliberately: the api derives the phase result from them (one F fails the drill) and
// refuses a part-marked sheet, so it must carry the steps rather than just the outcome the
// caller computed.
//
// Shared by the JPR checklist dialog and the mark-all-passed shortcut (see SignOffMatrix)
// so the two can't record the same evaluation two different ways.
export const practicalSignOffParams = ({sectionKey, uid, chapter, code, phase, result, marks, safetyFail = false, record}) => ({
    uid,
    sectionKey,
    kind: 'practical',
    chapter,
    code,
    // A retest keeps the first test's FAIL — it's a second evaluation of the same skill,
    // not a replacement for the first one's record.
    firstResult: phase === 'retest' ? (record?.firstTest?.result || 'FAIL') : result,
    retestResult: phase === 'retest' ? result : null,
    ...(phase === 'retest' ? {retestSteps: marks} : {firstSteps: marks}),
    safetyFail
});

// Sign off (or reopen) a whole section — sets the Viera level cell
// skills.{sectionKey}.status. Server enforces "fully demonstrated" first.
// signedName is the evaluator's typed page-session signature (#1451).
export const completeSection = (uid, sectionKey, complete = true, signedName = null) =>
    callFunction('signOffSkillDrill', {uid, sectionKey, kind: 'section', complete, ...(signedName ? {signedName} : {})});

// Record that a member already holds a qualification level earned elsewhere, crediting the
// level, every rung below it, and the skill cells the officer confirmed. `cells` is
// [{level, skillKey}] and must be a subset of impliableCells() — the server refuses
// anything wider. `complete: false` unwinds exactly what a previous credit wrote. See #1622.
export const creditQualificationLevel = ({uid, levelKey, completedDate, cells, complete = true, signedName = null}) =>
    callFunction('creditQualificationLevel', {
        uid,
        levelKey,
        complete,
        ...(completedDate ? {completedDate: ensureJSDates(completedDate)} : {}),
        ...(cells ? {cells} : {}),
        ...(signedName ? {signedName} : {})
    });

// Record the prerequisite qualifications a skill sign-off subsumes — Hazmat Ops implies
// Hazmat Awareness, ICS 400 implies 300/200/100. `cells` is [{level, skillKey}] and must be
// a subset of impliedSkillCells() for the skill named by `level`/`skillKey`; the server
// refuses anything wider. `complete: false` unwinds exactly what a previous credit wrote.
// See #1478.
export const creditImpliedSkills = ({uid, level, skillKey, completedDate, cells, complete = true, signedName = null}) =>
    callFunction('creditImpliedSkills', {
        uid,
        level,
        skillKey,
        complete,
        ...(completedDate ? {completedDate: ensureJSDates(completedDate)} : {}),
        ...(cells ? {cells} : {}),
        ...(signedName ? {signedName} : {})
    });

// Hide (or restore) members on a section's roster + matrix — a per-(member,
// section) display flag stored at users/{uid}.skillDrillsHidden.{sectionKey}.
// Distinct from signing the section off; see the api's setSkillDrillHidden (#1099).
export const setSkillDrillHidden = (uids, sectionKey, hidden) =>
    callFunction('setSkillDrillHidden', {uids, sectionKey, hidden});

// Is this member hidden from the given section's roster?
export const isSectionHidden = (user, section) =>
    Boolean(section && user?.skillDrillsHidden?.[section.key]);

// --- presentation helpers -------------------------------------------------

const AVATAR_COLORS = ['#b4561d', '#1d6fb4', '#2f8a4e', '#8a2f6f', '#9a7b1d', '#3a322d', '#4f46a8', '#1d8a8a'];

export const initialsOf = user => {
    const name = user?.fullName || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || '?';
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?';
};

export const avatarColor = user => {
    const key = user?.uid || user?.id || '';
    let hash = 0;
    for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
    return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

// Heat colour for a progress percentage (matches the design's green→red ramp).
export const heatColor = pct => {
    if (pct >= 80) return 'hsl(120,44%,38%)';
    if (pct >= 60) return 'hsl(95,42%,40%)';
    if (pct >= 40) return 'hsl(45,80%,38%)';
    if (pct >= 20) return 'hsl(28,80%,45%)';
    return 'hsl(2,72%,52%)';
};

// `chip` is the MUI palette colour a Chip should use, matching the authored-requirement
// Assignments page (ASSIGNED → default, IN_PROGRESS → info, COMPLETE → success) so the two
// grids read identically and the theme resolves contrast text for us. Pass it as the
// Chip's `color` prop rather than styling bg/colour through `sx`: the theme's MuiChip
// override targets `& .MuiChip-label` for the default colour, which outranks a root-level
// sx `color` — that's what left the green Complete chip with dark grey label text.
// `bg`/`color` remain for the non-Chip pill in SignOffMatrix's member header.
export const STATE_META = {
    'not-started': {label: 'Not started', chip: 'default', color: '#57534e', bg: '#e7e5e4'},
    'in-progress': {label: 'In progress', chip: 'info', color: '#fff', bg: '#ff9800'},
    complete: {label: 'Complete', chip: 'success', color: '#fff', bg: '#2e7d32'}
};

// "1–4" for a run, "3, 4" for a pair: a J&B module exam covers four or five chapters,
// and listing them all out crowded the exam row into two lines.
export const chapterRange = chapters => {
    const runs = [];
    for (const n of chapters) {
        const run = runs[runs.length - 1];
        if (run && n === run[run.length - 1] + 1) run.push(n);
        else runs.push([n]);
    }
    return runs.map(run => (run.length > 2 ? `${run[0]}\u2013${run[run.length - 1]}` : run.join(', '))).join(', ');
};
